import { PlusIcon } from '@langchain/untitled-ui-icons';
import { LinearProgress } from '@mui/joy';
import Button from '@mui/joy/Button';

import { useState } from 'react';
import { Link, NavigateFunction, useNavigate } from 'react-router-dom';

import { AnnotationQueueCrudPane } from '@/components/AnnotationQueuePopover/AnnotationQueueCrudPane';
import Breadcrumbs from '@/components/Breadcrumbs';
import EmptyState from '@/components/EmptyState';
import { ErrorBanner } from '@/components/ErrorBanner';
import { PageTitle } from '@/components/PageTitle';
import useToast from '@/components/Toast/useToast';
import {
  ANNOTATION_QUEUES_PAGE_DESCRIPTION,
  ANNOTATION_QUEUES_PAGE_TITLE,
  PROJECTS_PAGE_TITLE,
} from '@/constants/pageTitlesAndDescriptionConstants';
import { useColorScheme } from '@/hooks/useColorScheme';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';
import { useOrganizationId } from '@/hooks/useSwr';
import { AnnotationQueueSchema } from '@/types/schema';

import {
  appAnnotationQueuesPath,
  appOrganizationPath,
} from '../../utils/constants';
import { AnnotationQueuesTable } from '../AnnotationQueue/AnnotationQueuesTable';

export function AnnotationQueuesTableEmptyState(props: {
  organizationId?: string | null;
  navigate: NavigateFunction;
  isDarkMode: boolean;
  hasTags: boolean;
}) {
  return (
    <div className="flex h-[600px] w-full flex-1 flex-col items-start p-6">
      <div className="flex h-full w-full flex-col items-center justify-center gap-6">
        <EmptyState
          title="No annotation queues found"
          description="Start by bulk adding runs to a queue from a project
"
        />
        {props.organizationId && (
          <Button
            variant="outlined"
            color="neutral"
            onClick={() => {
              props.navigate(
                `/${appOrganizationPath}/${props.organizationId}/projects`
              );
            }}
          >
            Go to {PROJECTS_PAGE_TITLE}
          </Button>
        )}
      </div>
    </div>
  );
}

const AnnotationQueues = () => {
  const organizationId = useOrganizationId();
  const navigate = useNavigate();
  const { isDarkMode } = useColorScheme();
  const { authorize, isLoading: isPermsLoading } = usePermissions();

  const [
    isCreateAnnotationQueueModalOpen,
    setIsCreateAnnotationQueueModalOpen,
  ] = useState(false);
  const { createToast } = useToast();
  const { selectedTags } = useStoredResourceTags();

  if (isPermsLoading) {
    return <LinearProgress />;
  }

  if (!authorize('annotation-queues:read')) {
    return <ErrorBanner className="m-4">Forbidden</ErrorBanner>;
  }
  return (
    <div className="px-4 pb-6 pt-3">
      <Breadcrumbs />

      <div className="my-4 flex grow items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <PageTitle
            className="py-0"
            description={ANNOTATION_QUEUES_PAGE_DESCRIPTION}
          >
            {ANNOTATION_QUEUES_PAGE_TITLE}
          </PageTitle>
        </div>
        {authorize('annotation-queues:create') && (
          <Button
            size="sm"
            color="primary"
            onClick={() => setIsCreateAnnotationQueueModalOpen(true)}
            startDecorator={<PlusIcon className="size-4" />}
          >
            New Annotation Queue
          </Button>
        )}
        <AnnotationQueueCrudPane
          open={isCreateAnnotationQueueModalOpen}
          closeModal={() => setIsCreateAnnotationQueueModalOpen(false)}
          onSuccess={(aq: AnnotationQueueSchema) => {
            createToast({
              title: 'Created Annotation Queue',
              description: (
                <Link
                  to={`/${appOrganizationPath}/${organizationId}/${appAnnotationQueuesPath}/${aq.id}`}
                  className="underline"
                >
                  View the queue
                </Link>
              ),
            });
          }}
        />
      </div>
      <AnnotationQueuesTable
        filter={{}}
        emptyState={
          <AnnotationQueuesTableEmptyState
            navigate={navigate}
            organizationId={organizationId}
            isDarkMode={isDarkMode}
            hasTags={selectedTags.length > 0}
          />
        }
      />
    </div>
  );
};

export default AnnotationQueues;
